import { FileText, Loader2, Upload } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateDemandReviewWorkspaceMutation,
  useStartWorkflowMutation,
  useUploadDemandMutation,
} from '@/services/api/demandReviewService';

interface CreateDemandReviewDialogProps {
  matterId: string;
  trigger?: React.ReactNode;
  onSuccess?: (workspaceId: string) => void;
}

export const CreateDemandReviewDialog: React.FC<
  CreateDemandReviewDialogProps
> = ({ matterId, trigger, onSuccess }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createWorkspace] = useCreateDemandReviewWorkspaceMutation();
  const [uploadDemand] = useUploadDemandMutation();
  const [startWorkflow] = useStartWorkflowMutation();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        // Auto-fill workspace name from filename if empty
        if (!workspaceName) {
          const nameWithoutExt = file.name.replace(/\.(docx|pdf)$/i, '');
          setWorkspaceName(`Review: ${nameWithoutExt}`);
        }
      }
    },
    [workspaceName],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/pdf': ['.pdf'],
    },
  });

  const handleSubmit = async () => {
    if (!selectedFile || !workspaceName.trim()) {
      toast.error('Please provide a name and upload a demand document');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Create the workspace
      toast.info('Creating review workspace...');
      const workspace = await createWorkspace({
        matterId,
        data: {
          name: workspaceName.trim(),
        },
      }).unwrap();

      // 2. Upload the demand document to the workspace
      toast.info('Uploading demand document...');
      await uploadDemand({
        matterId,
        workspaceId: workspace.id,
        file: selectedFile,
      }).unwrap();

      // 3. Start the review workflow
      toast.info('Starting review workflow...');
      await startWorkflow({
        matterId,
        workspaceId: workspace.id,
      }).unwrap();

      toast.success('Demand review started successfully!');
      setOpen(false);
      resetForm();

      // Callback or navigate
      if (onSuccess) {
        onSuccess(workspace.id);
      } else {
        navigate(`/matters/${matterId}/document-review/${workspace.id}`);
      }
    } catch (error) {
      console.error('Failed to create demand review:', error);
      toast.error('Failed to create demand review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setWorkspaceName('');
    setSelectedFile(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <FileText className="h-4 w-4" />
            New Demand Review
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Demand Review</DialogTitle>
          <DialogDescription>
            Upload an externally drafted demand letter (Word or PDF) to review
            citations and run proofreading.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Workspace Name */}
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Review Name</Label>
            <Input
              id="workspace-name"
              placeholder="e.g., Smith v Jones Demand Review"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Demand Document</Label>
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                transition-colors duration-200
                ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
                ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input {...getInputProps()} disabled={isSubmitting} />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    disabled={isSubmitting}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                  <div>
                    <p className="font-medium">
                      {isDragActive
                        ? 'Drop the file here'
                        : 'Drag & drop or click to upload'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Supports Word (.docx) and PDF files
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* What happens next */}
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <p className="font-medium mb-1">What happens next:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Document converted to reviewable format</li>
              <li>Bates citations automatically detected</li>
              <li>Proofreading rules applied</li>
              <li>Upload exhibits to verify citations</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedFile || !workspaceName.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Start Review'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
